import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Brain, AlertTriangle, Phone, Star, MessageSquare, Pill, Activity, RefreshCw, Send, ExternalLink, TrendingUp, TrendingDown } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  AI INSIGHTS — Phase 3 hub: today's risk, recare, pharmacy, outcomes
// ═══════════════════════════════════════════════════════════════════

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const AIInsightsPage = ({ API_BASE, currentUser }) => {
  const [tab, setTab] = useState('today');
  const [today, setToday] = useState([]);
  const [dormant, setDormant] = useState([]);
  const [pharma, setPharma] = useState({ summary: null, expiring: [], reorder: [] });
  const [outcomeStats, setOutcomeStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d, ps, pe, pr, os] = await Promise.all([
        axios.get(`${API_BASE}/predictions/today`),
        axios.get(`${API_BASE}/recare/list?months=6`),
        axios.get(`${API_BASE}/pharmacy-smart/dashboard`),
        axios.get(`${API_BASE}/pharmacy-smart/expiring?days=60`),
        axios.get(`${API_BASE}/pharmacy-smart/reorder`),
        axios.get(`${API_BASE}/outcomes/stats`),
      ]);
      setToday(t.data || []); setDormant(d.data || []);
      setPharma({ summary: ps.data, expiring: pe.data || [], reorder: pr.data || [] });
      setOutcomeStats(os.data || []);
    } catch {}
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const sendRecare = async (p) => {
    window.open(p.waLink, '_blank');
    try { await axios.post(`${API_BASE}/recare/sent`, { patientId: p._id, channel: 'whatsapp', sentBy: currentUser?.fullName || 'admin' }); } catch {}
  };

  const RISK_COLOR = { high: '#dc2626', medium: '#f59e0b', low: '#16a34a' };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Brain size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> AI Insights</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              Patient predictions · AI Recare · Pharmacy Smart · Treatment outcomes
            </p>
          </div>
          <button onClick={load} disabled={loading} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
            <RefreshCw size={14} style={{ marginRight: 4, ...(loading ? { animation: 'spin 1s linear infinite' } : {}) }} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {[
          { k: 'today', l: '🎯 Today\'s Risk', c: today.length },
          { k: 'recare', l: '📞 Recare Queue', c: dormant.length },
          { k: 'pharmacy', l: '💊 Pharmacy Smart', c: (pharma.summary?.expired?.count || 0) + (pharma.summary?.lowStock || 0) },
          { k: 'outcomes', l: '🩺 Outcome Stats', c: outcomeStats.length },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: '8px 14px', borderRadius: 8,
            border: `1.5px solid ${tab === t.k ? theme.primary : theme.border}`,
            background: tab === t.k ? theme.primary + '15' : 'white',
            color: tab === t.k ? theme.primary : theme.textMuted,
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>{t.l} <span style={{ marginLeft: 4, background: 'rgba(0,0,0,0.08)', padding: '1px 6px', borderRadius: 999, fontSize: 11 }}>{t.c}</span></button>
        ))}
      </div>

      {loading && <div style={{ ...cardStyle, padding: 30, textAlign: 'center' }}>Loading…</div>}

      {/* TODAY */}
      {!loading && tab === 'today' && (
        today.length === 0 ? (
          <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.textMuted }}>No appointments today.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {today.map(p => {
              const c = RISK_COLOR[p.riskBand];
              return (
                <div key={p.patientId} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${c}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 60, textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{p.noShowRisk}%</div>
                      <div style={{ fontSize: 9, color: theme.textMuted, fontWeight: 700 }}>NO-SHOW</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy }}>
                        {p.patientName} <span style={{ color: theme.textMuted, fontSize: 12 }}>· {p.appointment?.time || '—'}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: theme.textMuted }}>
                        {p.appointment?.treatment || ''} · {p.appointment?.doctor || ''}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: theme.textMuted, marginTop: 3, flexWrap: 'wrap' }}>
                        <span>{p.pastAppointments} past appts ({p.noShows} no-show)</span>
                        <span>Last visit: {p.daysSinceLastVisit > 365 ? `${Math.floor(p.daysSinceLastVisit / 30)}mo ago` : `${p.daysSinceLastVisit}d ago`}</span>
                        <span>Pending Tx: {p.pendingTreatments}</span>
                      </div>
                      {p.factors.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 11.5, color: c, fontStyle: 'italic' }}>⚠ {p.factors.slice(0, 2).join(' · ')}</div>
                      )}
                    </div>
                    <div style={{ minWidth: 70, textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: theme.primary }}>{p.retentionScore}</div>
                      <div style={{ fontSize: 9, color: theme.textMuted, fontWeight: 700 }}>RETAIN</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* RECARE */}
      {!loading && tab === 'recare' && (
        dormant.length === 0 ? (
          <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.textMuted }}>No dormant patients ✓</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {dormant.map(p => (
              <div key={p._id} style={{ ...cardStyle, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: theme.navy }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: theme.textMuted }}>
                      {p.uhid || ''} · {p.phone || '—'} · last visit {p.monthsSince}+ months ago
                    </div>
                  </div>
                  <button onClick={() => sendRecare(p)} style={{ ...btnStyle, marginTop: 0, padding: '7px 14px', background: '#25D366' }}>
                    <Send size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> WhatsApp
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* PHARMACY */}
      {!loading && tab === 'pharmacy' && pharma.summary && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
            <PhStat icon={<AlertTriangle color="#dc2626" />} label="Expired" value={pharma.summary.expired.count} />
            <PhStat icon={<AlertTriangle color="#f59e0b" />} label="Expiring 30d" value={pharma.summary.expiring30} />
            <PhStat icon={<Pill color="#7c3aed" />} label="Total Items" value={pharma.summary.totalItems} />
            <PhStat icon={<TrendingDown color="#dc2626" />} label="Low Stock" value={pharma.summary.lowStock} />
          </div>
          <div style={{ ...cardStyle, padding: 14, marginBottom: 10 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>⏰ Expiring (60 days)</h3>
            {pharma.expiring.length === 0 ? <p style={{ color: theme.textMuted, fontSize: 13 }}>All clear ✓</p> :
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {pharma.expiring.slice(0, 30).map(b => (
                  <div key={b._id} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: `1px solid ${theme.borderLight}`, fontSize: 12 }}>
                    <span style={{ flex: 1 }}><strong>{b.itemName}</strong> · batch {b.batchNo}</span>
                    <span style={{ color: b.daysToExpiry < 0 ? '#dc2626' : b.daysToExpiry <= 7 ? '#dc2626' : '#f59e0b', fontWeight: 700, marginRight: 8 }}>
                      {b.daysToExpiry < 0 ? `EXPIRED ${-b.daysToExpiry}d` : `${b.daysToExpiry}d`}
                    </span>
                    <span style={{ color: theme.textMuted }}>stock {b.stock} · ₹{b.stockValue}</span>
                  </div>
                ))}
              </div>
            }
          </div>
          <div style={{ ...cardStyle, padding: 14 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>🛒 Reorder Suggestions</h3>
            {pharma.reorder.length === 0 ? <p style={{ color: theme.textMuted, fontSize: 13 }}>No reorders needed</p> :
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {pharma.reorder.slice(0, 30).map(r => (
                  <div key={r.itemId} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: `1px solid ${theme.borderLight}`, fontSize: 12 }}>
                    <span style={{ flex: 1 }}><strong>{r.name}</strong> · stock {r.currentStock}/{r.reorderLevel}</span>
                    <span style={{ color: r.urgency === 'urgent' ? '#dc2626' : '#f59e0b', fontWeight: 700, marginRight: 8 }}>
                      {r.daysOfStock}d left
                    </span>
                    <span style={{ color: theme.primary, fontWeight: 700 }}>order: {r.suggestedReorderQty}</span>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      )}

      {/* OUTCOMES */}
      {!loading && tab === 'outcomes' && (
        outcomeStats.length === 0 ? (
          <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.textMuted }}>
            No outcome data yet. Record post-treatment follow-ups via Patient → Treatment Outcomes.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {outcomeStats.map(s => {
              const c = s.successRate >= 90 ? '#16a34a' : s.successRate >= 70 ? '#f59e0b' : '#dc2626';
              return (
                <div key={s.treatmentName} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${c}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ minWidth: 70, textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{s.successRate}%</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: theme.textMuted }}>SUCCESS</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: theme.navy }}>{s.treatmentName}</div>
                      <div style={{ fontSize: 11, color: theme.textMuted }}>
                        {s.total} cases · {s.breakdown.success} success · {s.breakdown.complication} complications · {s.breakdown.failure} failures
                      </div>
                      {s.avgPain != null && <div style={{ fontSize: 11, color: theme.textMuted }}>Avg pain: {s.avgPain}/10 · Satisfaction: {s.avgSatisfaction || '—'}/5</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const PhStat = ({ icon, label, value }) => (
  <div style={{ ...cardStyle, padding: 10 }}>
    <div>{icon}</div>
    <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: theme.navy }}>{value}</div>
  </div>
);

export default AIInsightsPage;
